'use client';
import { useMediaQuery } from '@mnd-frontend/hooks';
import { toBoolean } from '@mnd-frontend/ts';
import { MynewsdeskLogo, media } from '@mnd-frontend/ui';
import { getMarketByLang } from '@mnd-frontend/utils';
import { Squash as Hamburger } from 'hamburger-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { LanguageLinksContext } from '../../contexts/LanguageLinksContext';
import { type LeftMenu, type RightMenu, shouldIgnoreMenuRender } from '../../lib/menus';
import { ctaClickHandler, track } from '../../lib/tracker';
import { routes } from '../../routes';
import { formatUrl } from '../../utils/formatUrl';
import { type Locales, locales } from '../../utils/locale';
import LinkButton from '../LinkButton';
import { SrOnly } from '../SrOnly';

const Wrapper = styled.header<{ $mobile?: boolean }>`
  width: 100%;
  display: flex;
  gap: var(--spacing-large);
  background: var(--bg-color);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: ${({ $mobile }) =>
    $mobile
      ? '14px 5px 14px 17px'
      : 'var(--spacing-medium) min(var(--spacing-medium), var(--site-padding))'};
`;

const Nav = styled.nav`
  display: none;
  justify-content: space-between;
  gap: var(--spacing-large);

  @media (min-width: 1260px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
`;

const List = styled.ul`
  list-style: none;
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  column-gap: 28px;
  padding: 0;
  margin: 0;
`;

const ButtonsWrapper = styled.ul<{ $mobile?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${({ $mobile }) => ($mobile ? '12px' : '8px')};
  ${({ $mobile }) =>
    $mobile &&
    css`
      margin-top: var(--spacing-small);
      flex-direction: column;
      li {
        width: 100%;
      }
    `}
`;

const desktopSubMenuStyling = css`
  position: absolute;
  z-index: 1;
  width: max-content;
  top: 3.125rem;
  border-top: 4px solid #ff4473;
`;

const mobileSubMenuStyling = css<{ $open?: boolean; $maxHeight?: number }>`
  padding: 0;
  padding-left: var(--spacing-small);
  border-left: 3px solid var(--color-lightgray);
  border-radius: 0;
  overflow: hidden;
  max-height: 0;
  margin: 0 0 0 var(--spacing-x-small);
  transition:
    max-height var(--anim-speed) var(--easing),
    margin var(--anim-speed) var(--easing);
  ${({ $open, $maxHeight }) =>
    $open &&
    css`
      max-height: ${$maxHeight}px;
      display: flex;
      margin: var(--spacing-medium) 0 0 var(--spacing-x-small);
    `};
`;

const SubMenu = styled.ul<{
  $mobile?: boolean;
  $open?: boolean;
  $maxHeight?: number;
}>`
  list-style: none;
  display: flex;
  flex-direction: column;
  background: var(--color-white);
  gap: var(--spacing-small);
  padding: var(--spacing-medium);
  border-radius: 4px;
  max-width: 14rem;
  ${({ $mobile }) =>
    !$mobile &&
    css`
      box-shadow: 0 13px 21px rgba(0, 0, 0, 0.25);
    `}
  width: max-content;

  ${({ $mobile }) => ($mobile ? mobileSubMenuStyling : desktopSubMenuStyling)}
`;

const BigMenu = styled.ul<{ $mobile?: boolean; $open?: boolean }>`
  list-style: none;
  position: absolute;
  opacity: 1;
  visibility: hidden;
  box-shadow: 0 13px 21px rgba(0, 0, 0, 0.25);
  font-weight: normal;
  max-width: 45rem;
  padding: var(--spacing-medium);
  background: var(--color-white);
  ${({ $mobile }) => ($mobile ? mobileSubMenuStyling : desktopSubMenuStyling)}
`;

const BigMenuMainLink = styled.div`
  font-size: 22px;
`;

const BigMenuMainLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const BigMenuLinksContainer = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  gap: var(--grid-gap);
  grid-auto-columns: minmax(auto, 15rem);
  grid-auto-flow: column;
  grid-template-rows: repeat(var(--menu-rows, 2), max-content);
  font-weight: normal;
  margin-top: var(--spacing-medium);
`;

const BigMenuLinksAndDescription = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChevronDown = styled.div<{ $open?: boolean; $mobile?: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
  margin-left: var(--spacing-x-small);
  color: var(--text-color);

  > svg {
    width: 9px;
    height: 13px;
    transition-duration: 0.25s;
  }

  ${({ $open }) => $open && rotateStyling};
  ${({ $mobile }) =>
    $mobile &&
    css`
      > svg {
        width: 10px;
        height: 15px;
        margin: 0;
      }
    `}
`;

const desktopMenuItemStyling = css<{ $disableSvgRotate?: boolean }>`
  height: 40px;

  ${BigMenu},
  ${SubMenu} {
    opacity: 0;
    visibility: hidden;
    margin-top: 0;
    transition:
      opacity var(--anim-speed) var(--easing),
      margin-top var(--anim-speed) var(--easing);
  }

  &:hover {
    ${BigMenu},
    ${SubMenu} {
      opacity: 1;
      visibility: visible;
      margin-top: var(--spacing-small);
    }
    ${ChevronDown} {
      transform: ${({ $disableSvgRotate }) => ($disableSvgRotate ? 'none' : 'rotate(180deg)')};
    }
  }
`;

const mobileMenuItemStyling = css`
  flex-direction: column;
  align-items: flex-start;

  i {
    margin-left: auto;
  }
`;

const MenuItem = styled.li<{ $mobile?: boolean; $disableSvgRotate?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ $mobile }) => ($mobile ? '12px 0' : '0')};

  i {
    width: 16px;
    height: 16px;
  }

  ${ChevronDown} {
    transition: transform var(--anim-speed) var(--easing);
  }

  ${({ $mobile }) => ($mobile ? mobileMenuItemStyling : desktopMenuItemStyling)}
`;

const MenuItemRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const linkStyling = css`
  text-decoration: none;
  transition: color 0.25s ease;

  &:hover {
    color: var(--color-primary);
  }
`;

const MenuItemLink = styled(Link)<{ $subLink?: boolean; $mobile?: boolean; $active?: boolean }>`
  display: flex;
  align-items: center;
  font-weight: ${({ $mobile, $subLink }) =>
    $mobile && $subLink ? 'normal' : 'var(--font-weight-semi-bold)'};
  font-size: ${({ $mobile, $subLink }) => ($mobile && !$subLink ? '17px' : '15px')};
  text-decoration: none;
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--text-color)')};
  flex: ${({ $mobile }) => ($mobile ? '' : 1)};
  flex-shrink: 0;
  ${linkStyling}
`;

const MenuItemDescription = styled.div`
  color: var(--color-gray);
  font-size: var(--font-size-small);
  margin: 0;
  font-weight: 200;
`;

const LocaleLink = styled(Link)<{ $subLink?: boolean; $mobile?: boolean; $active?: boolean }>`
  display: flex;
  align-items: center;
  font-weight: ${({ $mobile, $subLink }) =>
    $mobile && $subLink ? 'normal' : 'var(--font-weight-semi-bold)'};
  font-size: 15px;
  text-decoration: none;
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--text-color)')};
  flex: ${({ $mobile }) => ($mobile ? '' : 1)};
  flex-shrink: 0;
  ${linkStyling}
`;

const MenuLocaleButton = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;

  > svg {
    width: 22px;
    height: 22px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
`;

const HamburgerWrapper = styled.div`
  display: block;
  margin-left: auto;

  @media (min-width: 1260px) {
    display: none;
  }
`;

const MobileMenu = styled.nav<{ $open: boolean }>`
  position: fixed;
  top: 76px;
  right: ${({ $open }) => ($open ? '0' : '-100%')};
  width: 100%;
  height: calc(100vh - 76px);
  background-color: var(--color-white);
  transition: right 0.3s ease;
  z-index: 99;
  overflow: auto;
  padding: 0 17px var(--spacing-medium) 17px;
  box-sizing: border-box;

  > ul {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  @media (min-width: 1260px) {
    display: none;
  }
`;

const MobileIconWrapper = styled.button`
  background: none;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  height: 25px;
`;

const rotateStyling = css`
  svg {
    transform: rotate(180deg);
  }
`;

const MenuButton = styled(LinkButton)`
  padding: 7px 18px;
  display: flex;
  width: 100%;
  justify-content: center;
  white-space: nowrap;
`;

const MobileMenuItem = ({
  link,
  pathIncludingLocale,
  openMobileSubMenu,
  setOpenMobileSubMenu,
  isBigMenu,
}: {
  link: LeftMenu[0];
  pathIncludingLocale: string;
  openMobileSubMenu: string[];
  setOpenMobileSubMenu: React.Dispatch<React.SetStateAction<string[]>>;
  isBigMenu: boolean;
}) => {
  const contentContainerRef = useRef<HTMLUListElement | null>(null);
  return (
    link.path &&
    link.label && (
      <MenuItem key={link.id} $mobile>
        <MenuItemRow>
          <MenuItemLink
            href={formatUrl(link.path)}
            target={link.target || undefined}
            $mobile
            $active={link.path === pathIncludingLocale}
            onClick={() => {
              track({
                event: 'main_navigation',
                traits: {
                  event_info: {
                    navigation_selection: link.label ?? '',
                    navigation_category: link.label ?? '',
                    burger_menu: 'open',
                    user_interaction: 'main_navigation_click',
                  },
                },
              });
            }}
            prefetch={false}
          >
            {link.label}
          </MenuItemLink>
          {(link.childItems?.nodes || []).length > 0 && (
            <MobileIconWrapper
              aria-expanded={openMobileSubMenu.includes(link.id)}
              aria-controls={link.id}
              aria-label="Toggle submenu"
              type="button"
              onClick={() =>
                (link.childItems?.nodes || []).length > 0 &&
                setOpenMobileSubMenu(
                  openMobileSubMenu.includes(link.id)
                    ? openMobileSubMenu.filter(id => id !== link.id)
                    : [...openMobileSubMenu, link.id],
                )
              }
            >
              <ChevronDown aria-hidden="true" $open={openMobileSubMenu.includes(link.id)} $mobile>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 1000 1000"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="currentColor"
                    d="M32 228C74 186 144 186 186 228L500 542 814 228C856 186 926 186 968 228 1010 270 1010 340 968 382L576 772C534 814 464 814 422 772L32 380C-10 338-10 270 32 228Z"
                  />
                </svg>
              </ChevronDown>
            </MobileIconWrapper>
          )}
        </MenuItemRow>
        {(link.childItems?.nodes || []).length > 0 && (
          <SubMenu
            id={link.id}
            $mobile
            $open={openMobileSubMenu.includes(link.id)}
            $maxHeight={contentContainerRef.current?.scrollHeight}
            ref={contentContainerRef}
          >
            {link.childItems?.nodes.map(subMenuItem => {
              return (
                subMenuItem.path &&
                subMenuItem.label && (
                  <React.Fragment key={subMenuItem.id}>
                    {isBigMenu ? (
                      <>
                        <li>
                          <MenuItemLink
                            href={formatUrl(subMenuItem.path)}
                            target={subMenuItem.target || undefined}
                            $subLink
                            $mobile
                            $active={subMenuItem.path === pathIncludingLocale}
                            onClick={() => {
                              track({
                                event: 'main_navigation',
                                traits: {
                                  event_info: {
                                    navigation_selection: subMenuItem.label ?? '',
                                    navigation_category: link.label ?? '',
                                    burger_menu: 'open',
                                    user_interaction: 'main_navigation_click',
                                  },
                                },
                              });
                            }}
                            prefetch={false}
                          >
                            {subMenuItem.label}
                          </MenuItemLink>
                        </li>
                        {subMenuItem.childItems?.nodes.map(
                          subSubMenuItem =>
                            subSubMenuItem.path &&
                            subSubMenuItem.label && (
                              <li key={subSubMenuItem.id}>
                                <MenuItemLink
                                  href={formatUrl(subSubMenuItem.path)}
                                  target={subSubMenuItem.target || undefined}
                                  $subLink
                                  $mobile
                                  $active={subSubMenuItem.path === pathIncludingLocale}
                                  onClick={() => {
                                    track({
                                      event: 'main_navigation',
                                      traits: {
                                        event_info: {
                                          navigation_selection: subSubMenuItem.label ?? '',
                                          navigation_category: link.label ?? '',
                                          burger_menu: 'open',
                                          user_interaction: 'main_navigation_click',
                                        },
                                      },
                                    });
                                  }}
                                  prefetch={false}
                                >
                                  {subSubMenuItem.label}
                                </MenuItemLink>
                              </li>
                            ),
                        )}
                      </>
                    ) : (
                      <li>
                        <MenuItemLink
                          href={formatUrl(subMenuItem.path)}
                          target={subMenuItem.target || undefined}
                          $subLink
                          $mobile
                          $active={subMenuItem.path === pathIncludingLocale}
                          onClick={() => {
                            track({
                              event: 'main_navigation',
                              traits: {
                                event_info: {
                                  navigation_selection: subMenuItem.label ?? '',
                                  navigation_category: link.label ?? '',
                                  burger_menu: 'open',
                                  user_interaction: 'main_navigation_click',
                                },
                              },
                            });
                          }}
                          prefetch={false}
                        >
                          {subMenuItem.label}
                        </MenuItemLink>
                      </li>
                    )}
                  </React.Fragment>
                )
              );
            })}
          </SubMenu>
        )}
      </MenuItem>
    )
  );
};

const MobileLocaleListComponent = ({
  openMobileSubMenu,
  currentLocale,
}: {
  openMobileSubMenu: string[];
  currentLocale?: {
    locale: string;
    label: string;
  };
}) => {
  const contentContainerRef = useRef<HTMLUListElement | null>(null);
  const languageLinks = useContext(LanguageLinksContext);

  return (
    <SubMenu
      $mobile
      $open={openMobileSubMenu.includes('locale')}
      $maxHeight={contentContainerRef.current?.scrollHeight}
      ref={contentContainerRef}
    >
      {locales?.map(({ locale, label }, index) => {
        const slug = languageLinks[locale] || '';
        return (
          <li key={index}>
            <LocaleLink
              href={`/${locale}${slug ? `/${slug}` : ''}/`}
              prefetch={false}
              $mobile
              $subLink
              $active={locale === currentLocale?.locale}
            >
              {label}
            </LocaleLink>
          </li>
        );
      })}
    </SubMenu>
  );
};

type RightMenuItemWithClickHandler = RightMenu[number] & {
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

const Header = ({
  leftMenuLinks,
  rightMenuLinks,
  currentLocale,
  renderLanguageSwitcher,
}: {
  leftMenuLinks?: LeftMenu;
  rightMenuLinks?: RightMenuItemWithClickHandler[];
  currentLocale?: {
    locale: Locales;
    label: string;
  };
  renderLanguageSwitcher: boolean;
}) => {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const languageLinks = useContext(LanguageLinksContext);
  const [openMobileSubMenu, setOpenMobileSubMenu] = useState<string[]>([]);
  let languageSwitcher = renderLanguageSwitcher;
  const isMobile = useMediaQuery(media.lessThan('normal'));
  const pathIncludingLocale = '/' + currentLocale?.locale + pathname;
  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Close menu on navigation
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const toggleMenu = useCallback(() => {
    setOpen(o => !o);
  }, []);

  useLayoutEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [open]);

  if (shouldIgnoreMenuRender(pathname)) {
    return null;
  }
  if (
    pathname.includes('try-brand-report') ||
    pathname.includes('monitor-reports-trial') ||
    pathname.includes('custom-tour') ||
    pathname.includes('product-tour')
  ) {
    languageSwitcher = false;
  }

  if (pathname.includes('try-brand-report')) {
    const utm = [
      `mkt=${getMarketByLang(currentLocale?.locale ?? 'en')}`,
      'utm_source=www.mynewsdesk.com',
      'utm_campaign=try_brand_report',
      'utm_content=header',
    ]
      .filter(Boolean)
      .join('&');
    const url = `${routes.tryMonitorReportsForFree(currentLocale?.locale || 'en')}?${utm}`;
    rightMenuLinks = [
      rightMenuLinks?.find(n => n.path?.includes('user/signin')),
      {
        id: 'try-brand-report',
        label: t('reports_try_bp_header_try_for_free'),
        target: '',
        path: url,
        translatedPath: url,
        cssClasses: ['button', 'button--ghost'],
        onClick: e => {
          e.preventDefault();
          const brand = new URL(window.location.href).searchParams.get('brand');
          window.location.href = `${url}${brand ? `&utm_term=${brand}` : ''}`;
        },
      } as RightMenuItemWithClickHandler,
    ].filter(toBoolean);
  }

  return (
    <>
      <Wrapper $mobile={isMobile}>
        <LogoLink prefetch={false} href={`/${currentLocale ? `${currentLocale.locale}/` : ''}`}>
          <MynewsdeskLogo width="162" height="28" aria-hidden="true" />
          <SrOnly>Mynewsdesk</SrOnly>
        </LogoLink>
        <Nav aria-label="desktop primary" aria-hidden={isMobile}>
          <List role="menubar">
            {leftMenuLinks?.map(link => {
              const isTopLevelMenu = link.parentDatabaseId === 0;
              if (!isTopLevelMenu) return null;

              const bigMenuMainLink = link.childItems?.nodes[0];
              const isBigMenu =
                link.childItems &&
                link.childItems.nodes.length === 1 &&
                bigMenuMainLink &&
                (bigMenuMainLink.childItems?.nodes || []).length > 0;

              return (
                <MenuItem key={link.id} role="menuitem">
                  {link.path && link.label && (
                    <MenuItemLink
                      href={formatUrl(link.path)}
                      target={link.target || undefined}
                      $active={link.path === pathIncludingLocale}
                      onClick={() => {
                        track({
                          event: 'main_navigation',
                          traits: {
                            event_info: {
                              navigation_selection: link.label ?? '',
                              navigation_category: link.label ?? '',
                              burger_menu: 'closed',
                              user_interaction: 'main_navigation_click',
                            },
                          },
                        });
                      }}
                      prefetch={false}
                    >
                      {link.label}
                      {(link.childItems?.nodes || []).length > 0 && (
                        <ChevronDown>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 1000 1000"
                            strokeWidth="1.5"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="currentColor"
                              d="M32 228C74 186 144 186 186 228L500 542 814 228C856 186 926 186 968 228 1010 270 1010 340 968 382L576 772C534 814 464 814 422 772L32 380C-10 338-10 270 32 228Z"
                            />
                          </svg>
                        </ChevronDown>
                      )}
                    </MenuItemLink>
                  )}
                  {isBigMenu ? (
                    <BigMenu>
                      {bigMenuMainLink.path && bigMenuMainLink.label && (
                        <MenuItem>
                          <MenuItemLink
                            href={formatUrl(bigMenuMainLink.path)}
                            target={bigMenuMainLink.target || undefined}
                            $subLink
                            $active={bigMenuMainLink.path === pathIncludingLocale}
                            onClick={() => {
                              track({
                                event: 'main_navigation',
                                traits: {
                                  event_info: {
                                    navigation_selection: bigMenuMainLink.label ?? '',
                                    navigation_category: link.label ?? '',
                                    burger_menu: 'closed',
                                    user_interaction: 'main_navigation_click',
                                  },
                                },
                              });
                            }}
                            prefetch={false}
                          >
                            <BigMenuMainLink>
                              <BigMenuMainLabel>
                                {bigMenuMainLink.label}
                                <svg
                                  width="17"
                                  height="17"
                                  viewBox="0 0 1000 1000"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M712.1 578.2L99 578.2C55.8 578.2 20.8 543.2 20.8 500 20.8 456.8 55.8 421.7 99 421.7L712.1 421.7 563.1 272.8C532.6 242.2 532.6 192.7 563.1 162.2 593.7 131.6 643.2 131.6 673.8 162.2L956.3 444.7C986.9 475.2 986.9 524.8 956.3 555.3L673.8 837.9C643.2 868.4 593.7 868.4 563.1 837.9 532.6 807.3 532.6 757.8 563.1 727.2L712.1 578.2Z"
                                    stroke="currentColor"
                                    fill="currentColor"
                                  />
                                </svg>
                              </BigMenuMainLabel>
                              <MenuItemDescription
                                dangerouslySetInnerHTML={{
                                  __html: bigMenuMainLink.description || '',
                                }}
                              />
                            </BigMenuMainLink>
                          </MenuItemLink>
                        </MenuItem>
                      )}
                      <BigMenuLinksContainer>
                        {bigMenuMainLink.childItems?.nodes.map(subsubMenuItem => {
                          return (
                            <React.Fragment key={subsubMenuItem.id}>
                              {subsubMenuItem.path && subsubMenuItem.label && (
                                <li>
                                  <MenuItemLink
                                    href={formatUrl(subsubMenuItem.path)}
                                    target={subsubMenuItem.target || undefined}
                                    $subLink
                                    $active={subsubMenuItem.path === pathIncludingLocale}
                                    onClick={() => {
                                      track({
                                        event: 'main_navigation',
                                        traits: {
                                          event_info: {
                                            navigation_selection: subsubMenuItem.label ?? '',
                                            navigation_category: link.label ?? '',
                                            burger_menu: 'closed',
                                            user_interaction: 'main_navigation_click',
                                          },
                                        },
                                      });
                                    }}
                                    prefetch={false}
                                  >
                                    <BigMenuLinksAndDescription>
                                      {subsubMenuItem.label}
                                      <MenuItemDescription>
                                        {subsubMenuItem.description}
                                      </MenuItemDescription>
                                    </BigMenuLinksAndDescription>
                                  </MenuItemLink>
                                </li>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </BigMenuLinksContainer>
                    </BigMenu>
                  ) : (
                    <>
                      {(link.childItems?.nodes || []).length > 0 && (
                        <SubMenu>
                          {link.childItems?.nodes.map(subMenuItem => {
                            return (
                              <React.Fragment key={subMenuItem.id}>
                                {subMenuItem.path && subMenuItem.label && (
                                  <li>
                                    <MenuItemLink
                                      href={formatUrl(subMenuItem.path)}
                                      target={subMenuItem.target || undefined}
                                      $subLink
                                      $active={subMenuItem.path === pathIncludingLocale}
                                      onClick={() => {
                                        track({
                                          event: 'main_navigation',
                                          traits: {
                                            event_info: {
                                              navigation_selection: subMenuItem.label ?? '',
                                              navigation_category: link.label ?? '',
                                              burger_menu: 'closed',
                                              user_interaction: 'main_navigation_click',
                                            },
                                          },
                                        });
                                      }}
                                      prefetch={false}
                                    >
                                      {subMenuItem.label}
                                    </MenuItemLink>
                                  </li>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </SubMenu>
                      )}
                    </>
                  )}
                </MenuItem>
              );
            })}
          </List>
          <ButtonsWrapper>
            <>
              {languageSwitcher && (
                <MenuItem $disableSvgRotate>
                  <MenuLocaleButton>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 1000 1000"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="currentColor"
                        d="M500 0C222.7 0 0 222.7 0 500 0 777.3 222.7 1000 500 1000 777.3 1000 1000 777.3 1000 500 1000 222.7 777.3 0 500 0ZM931.8 500C931.8 540.9 927.3 577.3 913.6 613.6H718.2C722.7 577.3 727.3 540.9 727.3 500 727.3 454.5 722.7 409.1 713.6 363.6H909.1C922.7 404.5 931.8 454.5 931.8 500ZM600 81.8C600 81.8 600 81.8 600 81.8 718.2 113.6 818.2 190.9 877.3 295.5H877.3C877.3 295.5 877.3 295.5 877.3 295.5 877.3 295.5 877.3 295.5 877.3 295.5H700C677.3 218.2 645.5 145.5 600 81.8M659.1 500C659.1 540.9 654.5 577.3 650 613.6H350C345.5 577.3 340.9 540.9 340.9 500 340.9 454.5 345.5 409.1 354.5 363.6H645.5C654.5 409.1 659.1 454.5 659.1 500ZM500 68.2C504.5 68.2 504.5 68.2 509.1 68.2 563.6 131.8 604.5 209.1 631.8 295.5H372.7C400 209.1 440.9 131.8 495.5 68.2 495.5 68.2 495.5 68.2 500 68.2ZM400 81.8C359.1 145.5 322.7 218.2 300 295.5H122.7C122.7 295.5 122.7 295.5 122.7 295.5 122.7 295.5 122.7 295.5 122.7 295.5H122.7C177.3 190.9 277.3 109.1 400 81.8 400 81.8 400 81.8 400 81.8M90.9 363.6H286.4C277.3 409.1 272.7 454.5 272.7 500 272.7 540.9 277.3 577.3 281.8 613.6H86.4C77.3 577.3 68.2 540.9 68.2 500 68.2 454.5 77.3 404.5 90.9 363.6ZM295.5 881.8C286.4 877.3 281.8 872.7 272.7 868.2 268.2 863.6 263.6 863.6 259.1 859.1 250 854.5 240.9 845.5 231.8 836.4 231.8 836.4 231.8 836.4 231.8 836.4 227.3 836.4 227.3 831.8 222.7 831.8 213.6 822.7 204.5 813.6 195.5 804.5 195.5 804.5 195.5 804.5 195.5 804.5 190.9 800 190.9 800 186.4 795.5 186.4 795.5 186.4 795.5 186.4 795.5 177.3 786.4 172.7 781.8 163.6 772.7 163.6 772.7 163.6 772.7 163.6 772.7 159.1 768.2 159.1 768.2 154.5 763.6 154.5 763.6 154.5 763.6 154.5 763.6 145.5 750 136.4 740.9 131.8 727.3 122.7 713.6 118.2 700 109.1 686.4H109.1C109.1 686.4 109.1 686.4 109.1 686.4 109.1 686.4 109.1 686.4 109.1 686.4H295.5C318.2 772.7 350 854.5 400 922.7 400 922.7 400 922.7 395.5 922.7 395.5 922.7 395.5 922.7 395.5 922.7 395.5 922.7 395.5 922.7 395.5 922.7 377.3 918.2 359.1 913.6 340.9 904.5 340.9 904.5 340.9 904.5 340.9 904.5 331.8 900 322.7 895.5 313.6 890.9 313.6 890.9 313.6 890.9 313.6 890.9 309.1 886.4 300 881.8 295.5 881.8ZM490.9 931.8C436.4 863.6 390.9 777.3 363.6 681.8H636.4C609.1 777.3 568.2 863.6 509.1 931.8M509.1 931.8L509.1 931.8C504.5 931.8 504.5 931.8 500 931.8S495.5 931.8 490.9 931.8L490.9 931.8M890.9 681.8L890.9 681.8C881.8 695.5 877.3 709.1 868.2 722.7 859.1 736.4 854.5 745.5 845.5 759.1 840.9 763.6 840.9 768.2 836.4 768.2 836.4 768.2 836.4 768.2 836.4 768.2 831.8 777.3 822.7 786.4 813.6 790.9 813.6 790.9 813.6 790.9 813.6 790.9 809.1 795.5 809.1 795.5 804.5 800 804.5 800 804.5 800 804.5 800 795.5 809.1 786.4 818.2 777.3 827.3 772.7 827.3 772.7 831.8 768.2 831.8 759.1 840.9 750 845.5 740.9 854.5 736.4 859.1 731.8 859.1 727.3 863.6 718.2 868.2 713.6 872.7 704.5 877.3 700 881.8 695.5 881.8 690.9 886.4 690.9 886.4 690.9 886.4 690.9 886.4 681.8 890.9 672.7 895.5 663.6 900 663.6 900 663.6 900 663.6 900 645.5 904.5 627.3 913.6 609.1 918.2 609.1 918.2 609.1 918.2 609.1 918.2 609.1 918.2 609.1 918.2 609.1 918.2 609.1 918.2 609.1 918.2 604.5 918.2 650 850 686.4 768.2 709.1 681.8H890.9C890.9 681.8 890.9 681.8 890.9 681.8 890.9 681.8 890.9 681.8 890.9 681.8Z"
                      />
                    </svg>
                  </MenuLocaleButton>
                  <SubMenu>
                    {locales.map(({ locale, label }) => {
                      const slug = languageLinks[locale] || '';
                      return (
                        <li key={locale}>
                          <LocaleLink
                            href={`/${locale}${slug ? `/${slug}` : ''}/`}
                            prefetch={false}
                            $active={locale === currentLocale?.locale}
                          >
                            {label}
                          </LocaleLink>
                        </li>
                      );
                    })}
                  </SubMenu>
                </MenuItem>
              )}
              {rightMenuLinks &&
                rightMenuLinks.map(
                  link =>
                    link.cssClasses?.join(' ') === '' &&
                    link.path &&
                    link.label && (
                      <MenuItem key={link.id}>
                        <MenuButton
                          id={`menu-link-${link.id}`}
                          href={formatUrl(link.path)}
                          target={link.target || ''}
                          variant={'text'}
                          onClick={e => {
                            ctaClickHandler('header')(e);
                            link.onClick?.(e);
                          }}
                        >
                          {link.label}
                        </MenuButton>
                      </MenuItem>
                    ),
                )}
            </>
            {rightMenuLinks &&
              rightMenuLinks.map(
                link =>
                  link.cssClasses?.join(' ') !== '' &&
                  link.path &&
                  link.label && (
                    <MenuItem key={link.id}>
                      <MenuButton
                        id={`menu-link-${link.id}`}
                        href={formatUrl(link.path)}
                        variant={
                          link.cssClasses?.join(' ') === 'button button--ghost'
                            ? 'secondary'
                            : link.cssClasses?.join(' ') === 'button'
                              ? 'primary'
                              : 'text'
                        }
                        onClick={e => {
                          ctaClickHandler('header')(e);
                          link.onClick?.(e);
                        }}
                      >
                        {link.label}
                      </MenuButton>
                    </MenuItem>
                  ),
              )}
          </ButtonsWrapper>
        </Nav>
        <HamburgerWrapper aria-hidden={!isMobile}>
          <Hamburger toggled={open} toggle={toggleMenu} size={24} color="#181646" label="menu" />
        </HamburgerWrapper>
      </Wrapper>
      <MobileMenu $open={open} aria-label="mobile primary" aria-hidden={!isMobile}>
        <ul>
          {leftMenuLinks?.map((link, index) => {
            const isTopLevelMenu = link.parentDatabaseId === 0;
            if (!isTopLevelMenu) return null;
            const bigMenuMainLink = link.childItems?.nodes[0];
            const isBigMenu =
              link.childItems?.nodes.length === 1 &&
              (bigMenuMainLink?.childItems?.nodes || []).length > 0;
            return (
              <MobileMenuItem
                key={index}
                link={link}
                openMobileSubMenu={openMobileSubMenu}
                setOpenMobileSubMenu={setOpenMobileSubMenu}
                pathIncludingLocale={pathIncludingLocale}
                isBigMenu={isBigMenu}
              />
            );
          })}

          <MenuItem $mobile>
            <MenuItemRow>
              <MenuItemLink href={`${currentLocale?.locale}/` || ''} $mobile prefetch={false}>
                {currentLocale?.label}
              </MenuItemLink>
              <MobileIconWrapper
                type="button"
                aria-expanded={openMobileSubMenu.includes('locale')}
                aria-label='toggle submenu "locale"'
                onClick={() =>
                  setOpenMobileSubMenu(
                    openMobileSubMenu.includes('locale')
                      ? openMobileSubMenu.filter(id => id !== 'locale')
                      : [...openMobileSubMenu, 'locale'],
                  )
                }
              >
                <ChevronDown $open={openMobileSubMenu.includes('locale')} $mobile>
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 1000 1000"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="currentColor"
                      d="M32 228C74 186 144 186 186 228L500 542 814 228C856 186 926 186 968 228 1010 270 1010 340 968 382L576 772C534 814 464 814 422 772L32 380C-10 338-10 270 32 228Z"
                    />
                  </svg>
                </ChevronDown>
              </MobileIconWrapper>
            </MenuItemRow>
            <MobileLocaleListComponent
              currentLocale={currentLocale}
              openMobileSubMenu={openMobileSubMenu}
            />
          </MenuItem>

          {rightMenuLinks?.map(
            link =>
              link.cssClasses?.join(' ') === '' && (
                <MenuItem $mobile key={link.id}>
                  {link.path && link.label && (
                    <MenuItemRow>
                      <MenuItemLink
                        key={link.id}
                        href={formatUrl(link.path)}
                        target={link.target || undefined}
                        $mobile
                        $active={link.path === pathIncludingLocale}
                        onClick={e => {
                          ctaClickHandler('header')(e);
                          link.onClick?.(e);
                        }}
                        prefetch={false}
                      >
                        {link.label}
                      </MenuItemLink>
                    </MenuItemRow>
                  )}
                </MenuItem>
              ),
          )}
        </ul>
        <ButtonsWrapper $mobile>
          {rightMenuLinks?.map(
            link =>
              link.path &&
              link.label &&
              link.cssClasses?.join('') !== '' && (
                <MenuItem key={link.id}>
                  <MenuButton
                    href={formatUrl(link.path)}
                    target={link.target || ''}
                    variant={
                      link.cssClasses?.join(' ') === 'button button--ghost'
                        ? 'secondary'
                        : link.cssClasses?.join(' ') === 'button'
                          ? 'primary'
                          : 'text'
                    }
                    onClick={e => {
                      ctaClickHandler('header')(e);
                      link.onClick?.(e);
                    }}
                  >
                    {link.label}
                  </MenuButton>
                </MenuItem>
              ),
          )}
        </ButtonsWrapper>
      </MobileMenu>
    </>
  );
};

export default Header;
